import React from 'react';
import styled from '@emotion/styled';
import clsx from 'clsx';

import { useTheme, Tooltip } from '@mui/material';
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Link from '@mui/material/Link';
import capitalize from 'lodash/capitalize';

import { AvatarGroup, Favorite, Label, Tag } from '@/components';
import Avatar from "@imminently/decisively-ui/Avatar";
import Icon from "@imminently/decisively-ui/Icon";


const Tags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-flow: row wrap;
`;

const MenuButton = ({ ref, ...props }) => <Button icon ref={ref} {...props} label="More actions"><Icon name="show-more" /></Button>;

const StyledCard = styled(Card)`
  width: var(--size);
  display: flex;
  flex-flow: column nowrap;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border);

  &:hover {
    box-shadow: ${props => props.theme.mixins.body.boxShadow};
  }

  &:hover .MuiCardHeader-action, .MuiCardHeader-action:has([data-state="open"]) {
    opacity: 1;
  }

  &.archive {
    background-color: transparent;
    border: 1px solid var(--foreground);
  }
`;

const StyledCardHeader = styled(CardHeader)`
  padding: 0;
  padding-bottom: 0.5rem;
  gap: 1rem;

  & .MuiCardHeader-avatar {
    margin: 0;
  }

  & .MuiCardHeader-action {
    margin: 0;
    flex-flow: row nowrap;
    justify-content: flex-end;
    opacity: 0;
  }

  & .MuiCardHeader-content {
    overflow: hidden;
  }
`;

const Description = styled.span`
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledCardContent = styled(CardContent)`
  display: flex;
  padding: 0.5rem 0;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
`;

const StyledCardActions = styled(CardActions)`
  padding: 0;
  padding-top: 0.5rem;
`;

const MarginLeftAuto = styled.div`
  margin-left: auto !important;
  cursor: pointer;
`;

const AvatarPopperProps = { modifiers: { offset: { offset: '0, -10' }, flip: { enabled: false } } };

/**
 * @deprecated Use the purpose-built card components instead:
 * `WorkspaceCard`, `ProjectCard`, `ReleaseCard`, `InterviewCard`, `BotCard`
 * from `@/common/cards`.
 */
export const ResourceCard = ({
  id,
  name,
  description,
  icon = '/',
  users,
  label,
  labelColour,
  tags,
  numResources,
  width,
  archive = false,
  menu,
  action,
  noAvatar,
  alt,
  // onNumResourcesClick = () => {},
}) => {
  const theme = useTheme();
  // const onNumResourcesClickHandler = React.useCallback(() => {
  //   onNumResourcesClick( id );
  // }, [ id, onNumResourcesClick ]);

  // check if menu is a react component that returns null

  const actions = menu ? (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <MenuButton />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {menu}
      </DropdownMenu.Content>
    </DropdownMenu>
  ) : null;

  const size = width
    ? {
      '--size': typeof width === 'string' ? width : `${width}px`,
    }
    : {};

  return (
    <StyledCard elevation={0} className={clsx({ archive })} style={size}>
      <StyledCardHeader
        avatar={noAvatar ? null : <Avatar alt={typeof name === "string" ? name : alt} name={typeof name === "string" ? name : alt} size='lg' />}
        action={actions}
        title={name}
        subheader={description}
        titleTypographyProps={{ variant: 'h4', display: 'block', noWrap: true }}
        subheaderTypographyProps={{
          color: 'inherit',
          variant: 'caption',
          display: 'block',
          noWrap: true,
          component: Description,
        }}
      />
      <StyledCardContent>
        {label && <Label color={theme.mixins.labels[labelColour || label.toLowerCase()]}>{capitalize(label)}</Label>}
        <Tags>
          {tags && tags.slice(0, 5).map((t, i) => <Tag key={i} label={t} />)}
          {tags && tags.length > 5 && <Tag key='more tags' label={`+${tags.length - 5}`} />}
        </Tags>
        {numResources && (
          <MarginLeftAuto>
            <Link
              color='secondary'
              variant='caption'
              underline='always'
            // onClick={ onNumResourcesClickHandler }
            >
              {numResources}
            </Link>
          </MarginLeftAuto>
        )}
      </StyledCardContent>
      <StyledCardActions disableSpacing>
        <AvatarGroup spacing="small" size="md">
          {users && users.map(u => (
            <Tooltip key={u.id} title={u.name} placement="bottom" PopperProps={AvatarPopperProps}>
              <Avatar key={u.id} alt={u.name} name={u.name} size="md" />
            </Tooltip>
          ))}
        </AvatarGroup>
        {
          action ? (
            <MarginLeftAuto>
              <Button label={action.name} size='small' onClick={action.onClick}>
                {action.name}
              </Button>
            </MarginLeftAuto>
          ) : null
        }
      </StyledCardActions>
    </StyledCard>
  );
};

/**
 * @deprecated Use `CardSkeleton` from `@/common/cards` instead.
 */
export const ResourceLoading = ({ width = '512px' }) => {
  const size = {
    '--size': typeof width === 'string' ? width : `${width}px`,
  };
  return (
    <StyledCard style={size}>
      <StyledCardHeader
        avatar={<Skeleton variant="circular" width={48} height={48} />}
        action={(
          <>
            <Skeleton key='1' variant="circular" width={24} height={24} />
            <Skeleton key='2' variant="circular" width={24} height={24} />
          </>
        )}
        title={<Skeleton variant='text' width='50%' height={24} />}
        subheader={<Skeleton variant='text' width='80%' height={16} />}
      />
      <StyledCardContent>
        <Skeleton variant='text' width={100} height={24} />
        <Tags>
          <Skeleton key='1' variant='text' width={40} height={24} />
          <Skeleton key='2' variant='text' width={40} height={24} />
        </Tags>
        <MarginLeftAuto>
          <Skeleton variant='text' width={64} height={24} />
        </MarginLeftAuto>
      </StyledCardContent>
      <StyledCardActions disableSpacing>
        <AvatarGroup spacing='small'>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="circular" width={32} height={32} />
          ))}
        </AvatarGroup>
        <MarginLeftAuto>
          <Skeleton variant="rectangular" width={40} height={18} />
        </MarginLeftAuto>
      </StyledCardActions>
    </StyledCard>
  );
};
